/**
 * React Hooks for Computation Graph
 *
 * Convenient hooks for accessing graph values and metadata
 */

import { useMemo } from 'react';
import { useGraph } from './GraphContext';
import { NodeId, Node, DerivationTree, isAssumptionNode, isSourceNode } from '../types';

/**
 * Get a single node's value and metadata
 */
export function useNode(id: NodeId): {
  value: number | undefined;
  node: Node | undefined;
  isOverridden: boolean;
  override: (value: number) => void;
  clearOverride: () => void;
  inspect: () => void;
} {
  const ctx = useGraph();

  return useMemo(
    () => ({
      value: ctx.getValue(id),
      node: ctx.getNode(id),
      isOverridden: ctx.isOverridden(id),
      override: (value: number) => ctx.setOverride(id, value),
      clearOverride: () => ctx.clearOverride(id),
      inspect: () => ctx.setInspectedNodeId(id),
    }),
    [ctx, id]
  );
}

/**
 * Get just a value (minimal re-renders)
 */
export function useValue(id: NodeId): number | undefined {
  const ctx = useGraph();
  return ctx.values.get(id);
}

/**
 * Get multiple values at once
 */
export function useValues(ids: NodeId[]): Map<NodeId, number> {
  const ctx = useGraph();
  const idsKey = ids.join(',');

  return useMemo(() => {
    const result = new Map<NodeId, number>();
    for (const id of ids) {
      const val = ctx.values.get(id);
      if (val !== undefined) {
        result.set(id, val);
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.values, idsKey]);
}

/**
 * Get the derivation tree for a node
 */
export function useDerivation(id: NodeId): DerivationTree | undefined {
  const ctx = useGraph();
  return useMemo(() => ctx.getDerivation(id), [ctx, id]);
}

/**
 * Get nodes that depend on this node
 */
export function useDependents(id: NodeId): NodeId[] {
  const ctx = useGraph();
  return useMemo(() => ctx.getDependents(id), [ctx, id]);
}

/**
 * Get nodes that this node depends on
 */
export function useDependencies(id: NodeId): NodeId[] {
  const ctx = useGraph();
  return useMemo(() => ctx.getDeps(id), [ctx, id]);
}

/**
 * Get all assumption nodes for building control UIs
 */
export function useAssumptions(): Array<{
  id: NodeId;
  node: Node;
  value: number;
  isOverridden: boolean;
}> {
  const ctx = useGraph();

  return useMemo(() => {
    const result: Array<{
      id: NodeId;
      node: Node;
      value: number;
      isOverridden: boolean;
    }> = [];

    for (const [id, node] of Object.entries(ctx.graph.definition.nodes)) {
      if (isAssumptionNode(node)) {
        result.push({
          id,
          node,
          value: ctx.values.get(id) ?? node.defaultValue,
          isOverridden: ctx.overrides.has(id),
        });
      }
    }

    return result;
  }, [ctx.graph, ctx.values, ctx.overrides]);
}

/**
 * Get all source nodes for citation display
 */
export function useSources(): Array<{
  id: NodeId;
  node: Node;
  value: number;
}> {
  const ctx = useGraph();

  return useMemo(() => {
    const result: Array<{
      id: NodeId;
      node: Node;
      value: number;
    }> = [];

    for (const [id, node] of Object.entries(ctx.graph.definition.nodes)) {
      if (isSourceNode(node)) {
        result.push({
          id,
          node,
          value: ctx.values.get(id) ?? node.value,
        });
      }
    }

    return result;
  }, [ctx.graph, ctx.values]);
}

/**
 * Get the currently inspected node
 */
export function useInspectedNode(): {
  nodeId: NodeId | null;
  node: Node | undefined;
  value: number | undefined;
  derivation: DerivationTree | undefined;
  setInspectedNodeId: (id: NodeId | null) => void;
} {
  const ctx = useGraph();

  return useMemo(() => {
    const nodeId = ctx.inspectedNodeId;
    return {
      nodeId,
      node: nodeId ? ctx.getNode(nodeId) : undefined,
      value: nodeId ? ctx.getValue(nodeId) : undefined,
      derivation: nodeId ? ctx.getDerivation(nodeId) : undefined,
      setInspectedNodeId: ctx.setInspectedNodeId,
    };
  }, [ctx]);
}

/**
 * Format a value using the graph's formatValue function
 */
export function useFormatValue(): (
  value: number,
  unit: string,
  options?: { decimals?: number; compact?: boolean }
) => string {
  const ctx = useGraph();
  return ctx.formatValue;
}

/**
 * Check if any assumptions have been overridden
 */
export function useHasOverrides(): boolean {
  const ctx = useGraph();
  return ctx.overrides.size > 0;
}

/**
 * Get override management functions
 */
export function useOverrides(): {
  overrides: Map<NodeId, number>;
  setOverride: (id: NodeId, value: number) => void;
  clearOverride: (id: NodeId) => void;
  clearAllOverrides: () => void;
  hasOverrides: boolean;
} {
  const ctx = useGraph();

  return useMemo(
    () => ({
      overrides: ctx.overrides,
      setOverride: ctx.setOverride,
      clearOverride: ctx.clearOverride,
      clearAllOverrides: ctx.clearAllOverrides,
      hasOverrides: ctx.overrides.size > 0,
    }),
    [ctx]
  );
}

// Re-export popup hook for convenience
export { usePopup } from './hooks/usePopup';
export type { UsePopupOptions, UsePopupReturn } from './hooks/usePopup';
